import {action, computed, makeObservable, observable, runInAction} from 'mobx'
import {chatEventEmitter} from './connect-chat'

import {
  addToSet,
  lastOf,
  sortByLastActive,
  sortByLastMessage,
} from './connect-helpers'
import {ChatParticipant, Chats} from './index'
import type {ChatData, ChatMessage, WebSocketConnectionUser} from './models'
import {chatsWebsockets} from './socket.io/ChatsWebsockets'

class ChatsStore {
  private isReachedEnd = false
  private isFetchMore = false
  private currentVisibleChats = new Set<string>()
  private listeners: Array<() => void> = []
  user?: WebSocketConnectionUser
  currentParticipant?: ChatParticipant

  constructor() {
    makeObservable(this)
  }

  init = (currentParticipant: ChatParticipant) => {
    this.currentParticipant = currentParticipant
  }

  subscribeOnEvents = () => {
    this.listeners.push(
      chatEventEmitter.on('newMessage', this.didMessageReceive),
      chatEventEmitter.on('updateChatsList', this.didChatsReceived),
      chatEventEmitter.on('newMessagesCount', this.newMessagesCount),
      chatEventEmitter.on('blockUser', this.removeChatWithInterlocutorId),
    )
  }

  unsubscribeFromEvents = () => {
    this.listeners.forEach((cleaner) => cleaner())
  }

  startConnection = async () => {
    if (!this.currentParticipant)
      throw new Error('currentParticipant must be initialized')
    const params = await Chats.options.connectionParams()
    if (!params) return undefined

    this.user = params
    console.log(
      '🌏',
      '[ChatsStore.startConnection]',
      'chatsWebsockets.isInConnection',
      chatsWebsockets.isInConnection,
      'chatsWebsockets.isConnected',
      chatsWebsockets.isConnected,
      'user',
      this.user,
    )
    if (chatsWebsockets.isInConnection) return 'isInConnected'
    if (chatsWebsockets.isConnected) return undefined
    return chatsWebsockets.start(params)
  }

  @observable
  isInitialLoading = true

  @observable
  _isConnected = false

  @observable
  chats: ChatData[] = []

  @observable
  totalNewMessagesCount = 0

  @observable
  disabledAutoTranslationChats: Set<string> = new Set()

  @observable
  selectMode: boolean = false
  @observable
  selectedItems = new Set<string>()

  @computed
  get selectionInfo(): {
    selectMode: boolean
    selectedItems: Set<string>
  } {
    const selectMode = this.selectMode
    const selectedItems = selectMode ? this.selectedItems : new Set<string>()
    return {selectMode, selectedItems}
  }

  @action
  toggleSelectMode = () => {
    this.selectMode = !this.selectMode
    this.selectedItems.clear()
  }

  @action
  setItemSelected = (id: string, selected: boolean) => {
    if (selected) {
      this.selectedItems.add(id)
    } else {
      this.selectedItems.delete(id)
    }
  }

  @action
  disableChatAutoTranslation = (chatId: string) => {
    const chat = this.chats.find((c) => c.id === chatId)
    if (chat && chat.isTranslatable) {
      this.disabledAutoTranslationChats.add(chatId)
      this.saveDisabledAutoTranslationChatIds()
    }
  }

  @action
  enableChatAutoTranslation = (chatId: string) => {
    this.disabledAutoTranslationChats.delete(chatId)
    this.saveDisabledAutoTranslationChatIds()
  }

  @action
  newMessagesCount = (count: number) => {
    console.log('[ChatsStore.newMessagesCount]', count)
    runInAction(() => (this.totalNewMessagesCount = count))
  }

  @action
  removeChatWithInterlocutorId = (interlocutorId: string) => {
    console.log(
      '[ChatsStore.removeChatWithInterlocutorId]',
      'chatsLength',
      this.chats.length,
    )
    if (this.chats.length === 0) return
    runInAction(() => {
      this.chats = this.chats.filter(
        (c) => c.interlocutor.id !== interlocutorId,
      )
    })
  }

  @action
  setWsConnectionStatus = (connected: boolean) => {
    this._isConnected = connected
  }

  @computed
  get isConnected(): boolean {
    return this._isConnected
  }

  closeConnection = (cleanChats: boolean = false) => {
    console.log('[ChatsStore.closeConnection]', 'shouldCleanup', cleanChats)
    chatsWebsockets.cleanup()
    if (!cleanChats) return
    this.listeners.forEach((cleaner) => cleaner())
    if (this.chats.length === 0) return
    runInAction(() => {
      this.chats = []
      this._isConnected = false
      this.isInitialLoading = false
      this.isReachedEnd = false
      this.totalNewMessagesCount = 0
      this.isFetchMore = false
      this.currentVisibleChats.clear()
    })
  }

  didMessageReceive = async (message: ChatMessage) => {
    const {chatId} = message
    let chat: ChatData | undefined
    const localChat = this.chats.find((c) => c.id === chatId)
    if (!localChat) {
      chat = await chatsWebsockets.fetchChat(chatId)
    }
    const isSelf = message.ownerId === this.user?.hash

    if (chat) {
      runInAction(() => {
        // new Chat created
        this.chats = [...this.chats, chat as ChatData]
        this.chats.sort(sortByLastActive)
      })
    } else if (localChat) {
      // increment new messages and sort
      localChat.lastMessage = message
      localChat.newMessagesCount = isSelf
        ? localChat.newMessagesCount
        : localChat.newMessagesCount + 1
      localChat.lastMessage = message
      this.chats.sort(sortByLastMessage)
    }
  }

  didChatsReceived = (chats: Array<ChatData>) => {
    try {
      runInAction(() => {
        if (chats.length === 0 && this.isFetchMore) {
          this.isReachedEnd = true
          return
        }
        if (!this.isFetchMore) {
          this.chats = []
          this.currentVisibleChats.clear()
        }

        let c = chats.filter((c) => addToSet(this.currentVisibleChats, c.id))
        // this.populateAutoTranslatableChatsInfo(c)
        if (this.chats.length > 0) {
          c = c.concat(this.chats)
        }

        this.chats = c.sort(sortByLastActive)
      })
    } finally {
      runInAction(() => {
        this.isInitialLoading = false
      })
    }
  }

  deleteChat = async (chatId: string) => {
    await chatsWebsockets.deleteChat(chatId)
    runInAction(() => {
      this.chats = this.chats.filter((c) => c.id !== chatId)
      this.currentVisibleChats.delete(chatId)
    })
  }

  @action
  fetchMoreChats = () => {
    if (this.isReachedEnd) return
    if (this.isInitialLoading) return
    this.isFetchMore = true

    const lastChat = lastOf(this.chats)
    if (!lastChat) return
    chatsWebsockets.chatsList()
  }

  async createSupportChat(): Promise<ChatData | undefined> {
    return new Promise<any>(async (resolve) => {
      const chat = await chatsWebsockets.createSupportChat()
      if (!chat) return resolve(undefined)
      runInAction(() => {
        if (!addToSet(this.currentVisibleChats, chat.id)) return resolve(chat)
        const newChats = [chat]
        // this.populateAutoTranslatableChatsInfo(newChats)
        this.chats = this.chats.concat(newChats).sort(sortByLastActive)
        this.currentVisibleChats.add(chat.id)
        resolve(chat)
      })
    })
  }

  async createChat(params: {
    userHash: string
    productHash: string
  }): Promise<ChatData | undefined> {
    return new Promise(async (resolve) => {
      const chat = await chatsWebsockets.createChat(params)
      if (!chat) return resolve(undefined)
      runInAction(() => {
        if (!addToSet(this.currentVisibleChats, chat.id)) return resolve(chat)
        const newChats = [chat]
        // this.populateAutoTranslatableChatsInfo(newChats)
        this.chats = this.chats.concat(newChats).sort(sortByLastActive)
        this.currentVisibleChats.add(chat.id)
        resolve(chat)
      })
    })
  }

  @action
  updateUnreadMessageCountForChat = (chatId: string, newCount = 0) => {
    this.chats = this.chats.map((c) =>
      c.id === chatId ? {...c, newMessagesCount: newCount} : c,
    )
  }

  @action
  setDisabledAutoTranslationChatIds = (ids: Set<string>) => {
    this.disabledAutoTranslationChats = ids
  }

  @action
  // @ts-ignore
  private populateAutoTranslatableChatsInfo = (chats: Array<ChatData>) => {
    chats.forEach((chat) => {
      if (!chat.isTranslatable)
        this.disabledAutoTranslationChats.delete(chat.id)
    })
  }

  private saveDisabledAutoTranslationChatIds = () => {
    Chats.system.saveDisabledAutoTranslationChatIds(
      this.disabledAutoTranslationChats,
    )
  }
}
export const globalChatsStore = new ChatsStore()
